// pages/BankAccount/Edit/BankAccountEditPage.tsx
import { Button } from '@/components/ui/button';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BankAccountCreationForm from '@/components/organisms/BankAccountCreationForm';
import { bankAccountService } from '@/services/bank-accounts.service';
import { BankAccountFormValues } from '@/services/types/bank-accounts.types';
import toast from 'react-hot-toast';
import InfoModal from '@/components/molecules/InfoModal';

const BankAccountEditPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [defaultValues, setDefaultValues] = useState<BankAccountFormValues | null>(null);
  const { budgetId, bankAccountId } = useParams();
  const navigate = useNavigate();

  if (!budgetId || !bankAccountId) {
    console.error('Budget ID or Bank Account ID is missing from URL parameters');
    return <div>Error: Budget ID and Bank Account ID are required</div>;
  }

  // Fetch bank account data when component mounts
  useEffect(() => {
    const fetchBankAccount = async () => {
      try {
        setIsLoading(true);
        const response = await bankAccountService.getBankAccount(budgetId, bankAccountId);
        const bankAccount = response.data;

        // Transform API data to form values
        setDefaultValues({
          accountName: bankAccount.accountName,
          bankName: bankAccount.bankName,
          country: bankAccount.bankCountry,
          accountNumber: bankAccount.accountNumber,
          initialAmount: bankAccount.initialAmount.toString()
        });
      } catch (error) {
        console.error('Error fetching bank account:', error);
        toast.error('Failed to load bank account details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBankAccount();
  }, [budgetId, bankAccountId]);

  const handleSubmit = async (data: BankAccountFormValues) => {
    setIsSubmitting(true);
    try {
      // Transform form data to match API requirements
      const bankAccountData = {
        accountName: data.accountName,
        bankName: data.bankName,
        bankCountry: data.country,
        accountNumber: data.accountNumber,
        initialAmount: Number(data.initialAmount)
      };

      // Call API service to update bank account
      await bankAccountService.updateBankAccount(budgetId, bankAccountId, bankAccountData);

      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      console.log('Error updating bank account:', error);
      toast.error('Failed to update bank account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/budgets/${budgetId}/bank-accounts/${bankAccountId}`);
  };

  const handleButtonClick = () => {
    // Trigger form submission
    const form = document.getElementById('bank-account-creation-form') as HTMLFormElement;
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const handleGoToBankAccount = () => {
    setShowSuccessModal(false);
    // Navigate to the bank account detail page
    navigate(`/budgets/${budgetId}/bank-accounts/${bankAccountId}`);
  };

  if (isLoading) {
    return (
      <div className="bg-[#FBFBFF] p-4 flex flex-col items-center justify-center h-full">
        <p>Loading bank account details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-[#FBFBFF] p-4 flex flex-col flex-1 md:bg-white rounded-[16px] border">
        <h2 className="text-center font-bold mb-5 md:text-left md:text-[24px] md:mb-6">Edit Bank Account</h2>

        {/* FORM */}
        {defaultValues && (
          <BankAccountCreationForm
            onSubmit={handleSubmit}
            className="mb-6 flex-1"
            isSubmitting={isSubmitting}
            defaultValues={defaultValues}
            isEditMode={true}
          />
        )}

        {/* FOOTER */}
        <footer className="py-2 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent md:flex md:justify-end">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant={'text'}
            onClick={handleCancel}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            onClick={handleButtonClick}
            disabled={isSubmitting || isLoading}
            type="button"
          >
            {isSubmitting ? 'SAVING...' : 'SAVE'}
          </Button>
        </footer>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <InfoModal
          isOpen={showSuccessModal}
          variant="success"
          title="Bank Account Updated"
          message={<p>You have successfully updated the Bank Account!</p>}
          buttonText="Go to Bank Account"
          onButtonClick={handleGoToBankAccount}
        />
      )}
    </>
  );
};

export default BankAccountEditPage;
